// frontend/src/services/workspaceSwitchApi.js
// The super admin's workspace switcher (account menu). Entering a workspace
// is an audited impersonation of its admin: the server swaps only the access
// cookie and leaves the super admin's own refresh cookie in place, which is
// what /auth/impersonation/exit uses to bring them back without a new login.
import axios from "axios";
import { API_BASE_URL } from "../apiConstants";
import { getAuthHeader } from "./authHeader";
import { toCamel } from "../utils/caseConverter";

const PLATFORM_URL = `${API_BASE_URL}/platform`;
const AUTH_URL = `${API_BASE_URL}/auth`;

const failure = (error, fallback) => {
  const data = error?.response?.data;
  const wrapped = new Error(
    (typeof data?.error === "string" && data.error) || fallback,
  );
  wrapped.status = error?.response?.status;
  wrapped.code = data?.code;
  return wrapped;
};

const workspaceSwitchApi = {
  /** The super admin's own workspace first, then the demo workspaces. */
  list: async () => {
    try {
      const response = await axios.get(`${PLATFORM_URL}/workspaces`, getAuthHeader());
      const rows = toCamel(response.data?.data);
      return Array.isArray(rows) ? rows : [];
    } catch (error) {
      throw failure(error, "Workspaces could not be loaded.");
    }
  },

  /** Enter a workspace as its admin. The page reloads into it afterwards. */
  enter: async (organizationId) => {
    try {
      const response = await axios.post(
        `${PLATFORM_URL}/organizations/${encodeURIComponent(organizationId)}/enter`,
        {},
        getAuthHeader(),
      );
      return toCamel(response.data?.data ?? null);
    } catch (error) {
      throw failure(error, "That workspace could not be opened.");
    }
  },

  /** Leave the entered workspace and restore the super admin's own session. */
  exit: async () => {
    try {
      const response = await axios.post(
        `${AUTH_URL}/impersonation/exit`,
        {},
        getAuthHeader(),
      );
      return toCamel(response.data?.user ?? null);
    } catch (error) {
      throw failure(error, "Could not return to your workspace.");
    }
  },
};

export default workspaceSwitchApi;
